import React, { useCallback, useEffect, useState } from 'react';
import { adminApi } from '../../lib/adminApi';
import type { ApiWorkApplication } from '../../types/admin';
import { LoadingSpinner } from '../common/Loading';
import { ErrorMessage } from '../common/Error';
import { Button } from '../common/Button';
import { toast } from 'sonner';

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  approved: 'Aprovada',
  rejected: 'Rejeitada',
};

export const AdminApplicationsPage: React.FC = () => {
  const [applications, setApplications] = useState<ApiWorkApplication[]>([]);
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<{ email: string; password: string } | null>(null);

  const load = useCallback(async (filter: typeof status) => {
    setLoading(true);
    setError(null);
    try {
      const { applications: list } = await adminApi.listApplications(
        filter === 'all' ? undefined : filter
      );
      setApplications(list);
    } catch (err) {
      console.error('AdminApplications: erro ao listar', err);
      setError('Erro ao carregar candidaturas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(status);
  }, [status, load]);

  const handleApprove = async (application: ApiWorkApplication) => {
    if (!window.confirm(`Aprovar a candidatura de ${application.email}?`)) return;
    try {
      const { temporaryPassword } = await adminApi.approveApplication(application.id);
      setTempPassword({ email: application.email, password: temporaryPassword });
      load(status);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao aprovar candidatura');
      console.error('AdminApplications: approve falhou', err);
    }
  };

  const handleReject = async (application: ApiWorkApplication) => {
    if (!window.confirm(`Rejeitar a candidatura de ${application.email}?`)) return;
    try {
      await adminApi.rejectApplication(application.id);
      toast.success('Candidatura rejeitada');
      load(status);
    } catch (err) {
      // 409 = já processada (server só muda de 'pending')
      toast.error(err instanceof Error ? err.message : 'Erro ao rejeitar candidatura');
      console.error('AdminApplications: reject falhou', err);
    }
  };

  const handleResume = async (application: ApiWorkApplication) => {
    try {
      await adminApi.downloadResume(application.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Currículo não enviado ou removido');
      console.error('AdminApplications: download falhou', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <h2 className="text-2xl font-bold text-purple-200">Candidaturas</h2>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          className="input-primary md:w-48"
        >
          <option value="pending">Pendentes</option>
          <option value="approved">Aprovadas</option>
          <option value="rejected">Rejeitadas</option>
          <option value="all">Todas</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : error ? (
        <ErrorMessage message={error} />
      ) : (
        <div className="space-y-3">
          {applications.length === 0 && (
            <p className="text-purple-200/70 py-4 text-center">Nenhuma candidatura neste filtro</p>
          )}
          {applications.map((application) => (
            <div key={application.id} className="bg-white/5 rounded-lg p-4 space-y-2">
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-purple-200">
                    {application.name}{' '}
                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-200">
                      {STATUS_LABEL[application.status] ?? application.status}
                    </span>
                  </p>
                  <p className="text-sm text-purple-200/70">{application.email} · {application.phone ?? 'sem telefone'}</p>
                  <p className="text-xs text-purple-200/50">
                    Em {new Date(application.createdAt).toLocaleString('pt-BR')}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {application.status === 'pending' && (
                    <>
                      <Button size="sm" onClick={() => handleApprove(application)}>Aprovar</Button>
                      <Button size="sm" variant="secondary" onClick={() => handleReject(application)}>
                        Rejeitar
                      </Button>
                    </>
                  )}
                  {application.resumePath && (
                    <Button size="sm" variant="outline" onClick={() => handleResume(application)}>
                      Baixar currículo
                    </Button>
                  )}
                </div>
              </div>
              {application.experience && (
                <p className="text-sm text-purple-200/70 whitespace-pre-wrap">
                  {application.experience}
                </p>
              )}
              {application.motivation && (
                <p className="text-sm text-purple-200/50 whitespace-pre-wrap">
                  {application.motivation}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {tempPassword && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-indigo-950 rounded-lg p-6 max-w-md w-full border border-purple-500/30">
            <h3 className="text-lg font-bold text-purple-200 mb-2">
              Senha temporária gerada
            </h3>
            <p className="text-sm text-purple-200/70 mb-1">Login: {tempPassword.email}</p>
            <div className="bg-white/10 rounded-lg p-3 mb-3">
              <code className="text-purple-200 text-lg font-mono break-all">{tempPassword.password}</code>
            </div>
            <p className="text-sm text-red-300 mb-4">
              Copie AGORA. Esta senha não será mostrada de novo.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(tempPassword.password);
                    toast.success('Senha copiada');
                  } catch {
                    toast.error('Não foi possível copiar — selecione e copie manualmente');
                  }
                }}
              >
                Copiar
              </Button>
              <Button size="sm" onClick={() => setTempPassword(null)}>
                Fechei, copiei
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};